import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import { useAuth } from '@/auth/AuthContext';
import type { ConsultancyRow, UserRow, StudentRow } from '@/lib/types';
import { titleCase } from '@/lib/format';

const plans = [
  { key: 'basic', name: 'Basic', price: '₹2,999', period: '/month', counselors: 5, students: 100,
    features: ['Up to 5 Counselors', 'Up to 100 Students', 'Student CRM', 'Attendance & Leave', 'Appointment Calendar', 'Basic Reports'],
    missing: ['Lead Pipeline', 'Advanced Analytics'] },
  { key: 'pro', name: 'Pro', price: '₹6,999', period: '/month', counselors: 15, students: 500,
    features: ['Up to 15 Counselors', 'Up to 500 Students', 'Everything in Basic', 'Lead Pipeline', 'Follow-ups', 'Advanced Reports'],
    missing: ['Priority Support'], highlighted: true },
  { key: 'premium', name: 'Premium', price: '₹12,999', period: '/month', counselors: Infinity, students: Infinity,
    features: ['Unlimited Counselors', 'Unlimited Students', 'Everything in Pro', 'Priority Support', 'Multi-branch', 'Custom Reports'],
    missing: [] },
];

export default function SubscriptionPage() {
  const { refreshProfile } = useAuth();
  const { data: consultancy, loading, reload } = useApiData<ConsultancyRow>(() => api.consultancy());
  const { data: users } = useApiData<UserRow[]>(() => api.users());
  const { data: students } = useApiData<StudentRow[]>(() => api.students());
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [saving, setSaving] = useState('');

  const planKey = (consultancy?.subscriptionPlan || 'trial').toLowerCase();
  const paidPlan = plans.find((p) => p.key === planKey);
  // On trial (or any non-paid plan) don't masquerade as a paid tier — show a Free Trial
  // card. Usage limits/features fall back to the entry (Basic) tier during the trial.
  const current = paidPlan ?? { ...plans[0], key: 'trial', name: 'Free Trial', price: '₹0', period: '' };
  const counselorsUsed = (users ?? []).length;
  const studentsUsed = (students ?? []).length;

  const switchPlan = async (key: string) => {
    setSaving(key);
    // Paying / upgrading moves them off the trial onto an active paid plan.
    try { await api.updateConsultancy({ subscriptionPlan: key, subscriptionStatus: 'active' }); await reload(); await refreshProfile(); setShowUpgrade(false); }
    catch { /* ignore */ } finally { setSaving(''); }
  };

  const pct = (used: number, limit: number) => (limit === Infinity ? 15 : Math.min(100, Math.round((used / limit) * 100)));

  // 7-day free trial banner: show while status is 'trial' and we have an end date.
  const isTrial = (consultancy?.subscriptionStatus || 'trial').toLowerCase() === 'trial';
  const trialEnd = consultancy?.trialEndsAt ? new Date(consultancy.trialEndsAt) : null;
  const daysLeft = trialEnd
    ? Math.ceil((trialEnd.getTime() - new Date(new Date().toDateString()).getTime()) / 86400000)
    : null;
  const trialExpired = daysLeft !== null && daysLeft <= 0;

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {isTrial && daysLeft !== null && (
          <div className={`rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border ${trialExpired ? 'bg-[#FCE8E6] border-[#F5C6C0]' : 'bg-[#FFF8E1] border-[#F3E2B3]'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${trialExpired ? 'bg-[#D93025]/10 text-[#D93025]' : 'bg-[#E8A317]/15 text-[#E8A317]'}`}>
                <i className={`text-lg ${trialExpired ? 'ri-alarm-warning-line' : 'ri-gift-line'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#3C3C3C]">
                  {trialExpired ? 'Your free trial has ended' : `You're on a 7-day free trial`}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {trialExpired
                    ? 'Upgrade to a paid plan to keep full access to your workspace.'
                    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left · trial ends ${trialEnd?.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}. Upgrade any time to continue without interruption.`}
                </p>
              </div>
            </div>
            <button onClick={() => setShowUpgrade(true)} className="px-4 py-2 bg-[#0B6E77] text-white text-xs font-bold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap shrink-0">Upgrade Now</button>
          </div>
        )}
        <div className="bg-[#0B6E77] rounded-xl p-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <span className="text-xs font-semibold bg-[#0B6E77] text-white px-2 py-0.5 rounded-full">Current Plan</span>
              <h2 className="text-2xl font-bold mt-2">{loading ? '…' : `${current.name} Plan`}</h2>
              <p className="text-white/60 text-sm mt-1">{current.price} {current.period} · Status: {titleCase(consultancy?.subscriptionStatus || 'trial')}</p>
            </div>
            <button onClick={() => setShowUpgrade(true)} className="px-5 py-2.5 bg-[#0B6E77] text-white text-sm font-bold rounded-lg hover:bg-[#c9a430] transition-colors cursor-pointer whitespace-nowrap">Change Plan</button>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><div className="flex items-center justify-between mb-1.5"><span className="text-xs text-white/70">Counselors Used</span><span className="text-xs font-semibold text-white">{counselorsUsed} / {current.counselors === Infinity ? '∞' : current.counselors}</span></div><div className="h-2 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-[#0B6E77] rounded-full" style={{ width: `${pct(counselorsUsed, current.counselors)}%` }} /></div></div>
            <div><div className="flex items-center justify-between mb-1.5"><span className="text-xs text-white/70">Students Used</span><span className="text-xs font-semibold text-white">{studentsUsed} / {current.students === Infinity ? '∞' : current.students}</span></div><div className="h-2 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-[#0B6E77] rounded-full" style={{ width: `${pct(studentsUsed, current.students)}%` }} /></div></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E1E5EA] p-5">
          <h3 className="text-sm font-semibold text-[#3C3C3C] mb-4">Included in Your Plan</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{current.features.map((feature) => <div key={feature} className="flex items-center gap-2"><div className="w-4 h-4 flex items-center justify-center text-green-500"><i className="ri-checkbox-circle-fill text-sm" /></div><span className="text-sm text-gray-700">{feature}</span></div>)}</div>
        </div>

        <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E1E5EA]"><h3 className="text-sm font-semibold text-[#3C3C3C]">Billing History</h3></div>
          <div className="p-10 text-center text-sm text-gray-400">No billing records yet. Payment integration is not enabled — plan changes here are for access control only.</div>
        </div>
      </div>

      {showUpgrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowUpgrade(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5EA]"><h3 className="text-base font-semibold text-[#3C3C3C]">Choose Your Plan</h3><button onClick={() => setShowUpgrade(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg" /></button></div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const isCurrent = plan.key === planKey;
                return (
                  <div key={plan.key} className={`rounded-xl border-2 p-4 ${plan.highlighted ? 'border-[#0B6E77] bg-amber-50/30' : 'border-[#E1E5EA]'}`}>
                    {plan.highlighted && <span className="text-[10px] font-bold bg-[#0B6E77] text-white px-2 py-0.5 rounded-full">Most Popular</span>}
                    <h4 className="text-base font-bold text-[#3C3C3C] mt-2">{plan.name}</h4>
                    <p className="text-xl font-bold text-[#3C3C3C]">{plan.price}<span className="text-xs font-normal text-gray-400">{plan.period}</span></p>
                    <div className="mt-3 space-y-1.5">{plan.features.map((f) => <div key={f} className="flex items-center gap-1.5"><i className="ri-checkbox-circle-fill text-green-500 text-xs" /><span className="text-xs text-gray-700">{f}</span></div>)}{plan.missing.map((f) => <div key={f} className="flex items-center gap-1.5 opacity-40"><i className="ri-close-circle-line text-gray-400 text-xs" /><span className="text-xs text-gray-500">{f}</span></div>)}</div>
                    <button disabled={isCurrent || saving === plan.key} onClick={() => switchPlan(plan.key)} className={`w-full mt-4 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${plan.highlighted ? 'bg-[#0B6E77] text-white hover:bg-[#085258]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{isCurrent ? 'Current Plan' : saving === plan.key ? 'Switching…' : `Switch to ${plan.name}`}</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
